import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * LiveAvailabilityTicker — homepage hero microcopy.
 *
 * Pulls aggregate availability from /api/availability/live (no PII) and
 * surfaces a small green ticker like:
 *
 *   🟢 LIVE NOW · 3 Concierges available this weekend
 *
 * Hides itself entirely when zero real Concierges exist (pre-launch),
 * so the homepage never lies about supply. Uses a smooth pulse on the
 * green dot for low-key, premium "now" energy — not aggressive urgency.
 */
export default function LiveAvailabilityTicker() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/availability/live")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  // Hide until we have a real-Concierge count > 0
  if (!data || !data.total_real_concierges || data.available_now <= 0) {
    return null;
  }

  const weekend = data.available_this_weekend;
  const now = data.available_now;
  // Prefer the weekend stat if it's non-zero (higher conversion intent);
  // otherwise show the broader 7-day count.
  const label = weekend > 0
    ? `${weekend} ${weekend === 1 ? "Concierge" : "Concierges"} available this weekend`
    : `${now} ${now === 1 ? "Concierge" : "Concierges"} available this week`;

  return (
    <div
      data-testid="live-availability-ticker"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#064E3B]/8 border border-[#064E3B]/15 text-[11px] font-semibold text-[#064E3B] tracking-wider uppercase"
    >
      <span className="relative inline-flex">
        <span className="w-2 h-2 rounded-full bg-[#22C55E]"/>
        <span className="absolute inset-0 w-2 h-2 rounded-full bg-[#22C55E] animate-ping opacity-60"/>
      </span>
      Live now · {label}
    </div>
  );
}
